// Seeded 3D simplex noise (after Stefan Gustavson's public domain reference).
// Returns values in roughly [-1, 1]. Pure math, safe to import anywhere.

export type Noise3D = (x: number, y: number, z: number) => number

const F3 = 1 / 3
const G3 = 1 / 6

const GRADIENTS = new Float32Array([
    1, 1, 0, -1, 1, 0, 1, -1, 0, -1, -1, 0,
    1, 0, 1, -1, 0, 1, 1, 0, -1, -1, 0, -1,
    0, 1, 1, 0, -1, 1, 0, 1, -1, 0, -1, -1
])

export const createRandom = (seed: number): (() => number) => {
    let state = seed >>> 0

    return () => {
        state = (state + 0x6d2b79f5) >>> 0
        let value = state
        value = Math.imul(value ^ (value >>> 15), value | 1)
        value ^= value + Math.imul(value ^ (value >>> 7), value | 61)

        return ((value ^ (value >>> 14)) >>> 0) / 4294967296
    }
}

export const createNoise3D = (seed = 1): Noise3D => {
    const random = createRandom(seed)
    const source = new Uint8Array(256)
    const perm = new Uint8Array(512)
    const permMod12 = new Uint8Array(512)

    for (let i = 0; i < 256; i++) source[i] = i

    for (let i = 255; i > 0; i--) {
        const j = Math.floor(random() * (i + 1))
        const swap = source[i]
        source[i] = source[j]
        source[j] = swap
    }

    for (let i = 0; i < 512; i++) {
        perm[i] = source[i & 255]
        permMod12[i] = perm[i] % 12
    }

    return (x, y, z) => {
        const skew = (x + y + z) * F3
        const i = Math.floor(x + skew)
        const j = Math.floor(y + skew)
        const k = Math.floor(z + skew)
        const unskew = (i + j + k) * G3
        const x0 = x - (i - unskew)
        const y0 = y - (j - unskew)
        const z0 = z - (k - unskew)

        let i1 = 0
        let j1 = 0
        let k1 = 0
        let i2 = 0
        let j2 = 0
        let k2 = 0

        if (x0 >= y0) {
            if (y0 >= z0) {
                i1 = 1; i2 = 1; j2 = 1
            }
            else if (x0 >= z0) {
                i1 = 1; i2 = 1; k2 = 1
            }
            else {
                k1 = 1; i2 = 1; k2 = 1
            }
        }
        else if (y0 < z0) {
            k1 = 1; j2 = 1; k2 = 1
        }
        else if (x0 < z0) {
            j1 = 1; j2 = 1; k2 = 1
        }
        else {
            j1 = 1; i2 = 1; j2 = 1
        }

        const x1 = x0 - i1 + G3
        const y1 = y0 - j1 + G3
        const z1 = z0 - k1 + G3
        const x2 = x0 - i2 + 2 * G3
        const y2 = y0 - j2 + 2 * G3
        const z2 = z0 - k2 + 2 * G3
        const x3 = x0 - 1 + 3 * G3
        const y3 = y0 - 1 + 3 * G3
        const z3 = z0 - 1 + 3 * G3

        const ii = i & 255
        const jj = j & 255
        const kk = k & 255

        let total = 0

        let t0 = 0.6 - x0 * x0 - y0 * y0 - z0 * z0
        if (t0 > 0) {
            const g = permMod12[ii + perm[jj + perm[kk]]] * 3
            t0 *= t0
            total += t0 * t0 * (GRADIENTS[g] * x0 + GRADIENTS[g + 1] * y0 + GRADIENTS[g + 2] * z0)
        }

        let t1 = 0.6 - x1 * x1 - y1 * y1 - z1 * z1
        if (t1 > 0) {
            const g = permMod12[ii + i1 + perm[jj + j1 + perm[kk + k1]]] * 3
            t1 *= t1
            total += t1 * t1 * (GRADIENTS[g] * x1 + GRADIENTS[g + 1] * y1 + GRADIENTS[g + 2] * z1)
        }

        let t2 = 0.6 - x2 * x2 - y2 * y2 - z2 * z2
        if (t2 > 0) {
            const g = permMod12[ii + i2 + perm[jj + j2 + perm[kk + k2]]] * 3
            t2 *= t2
            total += t2 * t2 * (GRADIENTS[g] * x2 + GRADIENTS[g + 1] * y2 + GRADIENTS[g + 2] * z2)
        }

        let t3 = 0.6 - x3 * x3 - y3 * y3 - z3 * z3
        if (t3 > 0) {
            const g = permMod12[ii + 1 + perm[jj + 1 + perm[kk + 1]]] * 3
            t3 *= t3
            total += t3 * t3 * (GRADIENTS[g] * x3 + GRADIENTS[g + 1] * y3 + GRADIENTS[g + 2] * z3)
        }

        return 32 * total
    }
}
